import { Text, Icon, Switch, HStack, VStack } from '@genial/components';
import { BoxRadius, Color, Spacing, Typography } from '@genial/design-tokens';
import { Box } from 'native-base';
import React from 'react';

import { RiskRating } from '../../../../../../components/RiskRating';
import { TableContentItemText } from '../../../../../../components/TableContent';
import { ProductsDataDetailsProps, ProductsDataProps } from '../../../../utils/types';
import { TermsAndDocumentsButton } from '../TermsAndDocumentsButton';

import { getObservation, shouldRenderTermsButton } from './constants';

export interface IProductTable {
  productData: ProductsDataProps;
  handleToggleOrderStatus: (id: string) => void;
}

export const ProductTable: React.FC<IProductTable> = ({ productData, handleToggleOrderStatus }) => {
  const hasTermsAndDocuments = shouldRenderTermsButton(productData);
  const observation = getObservation(productData);

  const renderDailyVariation = ({ value, icon }: ProductsDataDetailsProps) => {
    const valueDailyVariation = value.split('%');

    return (
      <HStack flexDirection={'row'}>
        <Icon
          name={icon!}
          color={
            icon === 'ArrowUp' ? Color['feedback.success.default'] : Color['feedback.error.default']
          }
          size="xxxs"
        />
        <TableContentItemText
          fontWeight={'bold'}
          color={
            icon === 'ArrowUp' ? Color['feedback.success.default'] : Color['feedback.error.default']
          }
          paddingRight={Spacing['inline.quarck']}>
          {valueDailyVariation[0]}%
        </TableContentItemText>
        <TableContentItemText color="neutral.dark">{valueDailyVariation[1]}</TableContentItemText>
      </HStack>
    );
  };

  const renderProductDetailsValue = (item: ProductsDataDetailsProps) => {
    if (item.icon) {
      return renderDailyVariation(item);
    }

    if (item.type === 'risk') {
      return (
        <RiskRating
          currentRiskRating={(item.value as unknown as number) || 0}
          tooltip={item.label}
        />
      );
    }

    return (
      <TableContentItemText
        color="neutral.black"
        {...(item.type === 'positive' && { color: Color['feedback.success.default'] })}
        {...(item.type === 'negative' && { color: Color['feedback.error.default'] })}>
        {item.value}
      </TableContentItemText>
    );
  };

  return (
    <VStack
      my={Spacing['inline.nano']}
      p={Spacing['inline.xxs']}
      bg={Color.background}
      borderRadius={BoxRadius.sm}
      w="full"
      direction="column"
      space={Spacing['inline.nano']}>
      <VStack space={Spacing['inline.xxxs']}>
        <HStack alignItems="center" justifyContent="space-between" w="full">
          <TableContentItemText
            color={Color['neutral.dark']}
            fontSize={Typography['fontsize.md']}
            variant="semibold">
            {productData.productCategory}
          </TableContentItemText>
          <Box
            bg={Color[productData.acceptance ? 'brand.primary.dark' : 'neutral.lightest']}
            rounded="full"
            w={Spacing['inline.sm']}
            h={Spacing['inline.xxs']}
            justifyContent="center"
            alignItems="center">
            <Switch
              isChecked={productData.acceptance}
              h={Spacing['inline.xxxs']}
              rounded="full"
              onToggle={() => {
                handleToggleOrderStatus(productData.id);
              }}
              bgColor={Color[productData.acceptance ? 'brand.primary.dark' : 'neutral.lightest']}
              onTrackColor={Color['brand.primary.dark']}
              offTrackColor={Color['neutral.lightest']}
              offThumbColor={Color['neutral.white']}
              onThumbColor={Color['neutral.white']}
              _hover={{
                trackColor: { false: Color['neutral.lightest'], true: Color['brand.primary.dark'] },
              }}
            />
          </Box>
        </HStack>
        <TableContentItemText fontSize={Typography['fontsize.lg']} variant="semibold">
          {productData.title}
        </TableContentItemText>
      </VStack>
      <Box my={Spacing['inline.xxxs']} h="0.5px" w="full" bg="neutral.lightest" />
      <VStack space={Spacing['stack.nano']} w="full">
        {productData.details.map((item, index) => (
          <VStack key={index}>
            <HStack alignItems="center" justifyContent="space-between">
              <Text
                color={item.style ? 'neutral.black' : 'neutral.medium'}
                fontWeight={item.style && 'semibold'}
                fontSize={Typography['fontsize.xs']}>
                {item.label}
              </Text>

              {renderProductDetailsValue(item)}
            </HStack>
            <Box my={Spacing['inline.nano']} h="0.5px" w="full" bg="neutral.lightest" />
          </VStack>
        ))}
        <HStack alignItems="center" justifyContent="space-between" w="full">
          <VStack>
            <Text color="neutral.medium">{productData.total.label}</Text>
            {productData.total.description && (
              <Text color="neutral.medium" fontSize={Typography['fontsize.xs']}>
                {productData.total.description}
              </Text>
            )}
          </VStack>
          <TableContentItemText
            fontWeight={'semibold'}
            color="neutral.black"
            fontSize={Typography['fontsize.lg']}>
            {productData.total.value}
          </TableContentItemText>
        </HStack>
      </VStack>

      {hasTermsAndDocuments && (
        <TermsAndDocumentsButton basketOfferFile={productData.basketOfferFiles} />
      )}

      {!!observation && (
        <Text
          fontSize={Typography['fontsize.xs']}
          color="neutral.black"
          pt={Spacing['inline.xxxs']}>
          {observation}
        </Text>
      )}
    </VStack>
  );
};
